import { z } from 'zod';
import { PerformanceBandSchema, StudentStatusSchema } from './enums';
import {
  EnrollmentNumberSchema,
  IdSchema,
  PersonDocumentSchema,
  PersonNameSchema,
} from './validation';

/** '' (campo vazio no formulário) = ausente; caso contrário valida RG/CPF com mensagem específica. */
const optionalDocument = z.preprocess(
  (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
  PersonDocumentSchema.optional(),
);

export const CreateStudentSchema = z.object({
  name: PersonNameSchema,
  document: optionalDocument,
  enrollmentNumber: EnrollmentNumberSchema,
  /** Turma inicial (opcional). Define série/ano letivo. */
  classId: IdSchema.optional(),
  guardian: z.object({
    name: PersonNameSchema,
    document: optionalDocument,
  }),
  address: z.object({
    fullAddress: z.string().trim().min(5, 'Informe o endereço').max(300),
  }),
  status: StudentStatusSchema.optional(),
});
export type CreateStudentInput = z.input<typeof CreateStudentSchema>;
export type CreateStudentData = z.output<typeof CreateStudentSchema>;

export const UpdateStudentSchema = z.object({
  name: PersonNameSchema.optional(),
  document: optionalDocument,
  guardian: z
    .object({
      name: PersonNameSchema,
      document: optionalDocument,
    })
    .optional(),
  address: z.object({ fullAddress: z.string().trim().min(5).max(300) }).optional(),
  status: StudentStatusSchema.optional(),
  /** Troca de turma: null remove o vínculo atual. */
  classId: IdSchema.nullable().optional(),
});
export type UpdateStudentInput = z.input<typeof UpdateStudentSchema>;

export const ListStudentsQuerySchema = z.object({
  search: z.string().trim().max(80).optional(),
  academicYearId: IdSchema.optional(),
  grade: z.string().trim().max(40).optional(),
  status: StudentStatusSchema.optional(),
  classId: IdSchema.optional(),
  /** Somente alunos sem turma ativa. */
  unassigned: z
    .union([z.boolean(), z.enum(['true', 'false']).transform((v) => v === 'true')])
    .optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});
export type ListStudentsQuery = z.input<typeof ListStudentsQuerySchema>;

export const StudentClassRefSchema = z.object({
  id: z.string(),
  name: z.string(),
  grade: z.string(),
  shiftLabel: z.string(),
});

export const StudentListItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  enrollmentNumber: z.string(),
  guardianName: z.string(),
  status: StudentStatusSchema,
  currentClass: StudentClassRefSchema.nullable(),
  hasAvatar: z.boolean(),
  averageScore: z.number().nullable(),
  performance: PerformanceBandSchema,
});
export type StudentListItem = z.infer<typeof StudentListItemSchema>;

export const StudentDetailSchema = z.object({
  id: z.string(),
  name: z.string(),
  enrollmentNumber: z.string(),
  /** Documento completo apenas para quem tem student:write; caso contrário, mascarado. */
  document: z.string().nullable(),
  guardian: z.object({ name: z.string(), document: z.string().nullable() }),
  address: z.object({ fullAddress: z.string() }),
  status: StudentStatusSchema,
  currentClass: StudentClassRefSchema.nullable(),
  academicYearId: z.string().nullable(),
  hasAvatar: z.boolean(),
  averageScore: z.number().nullable(),
  performance: PerformanceBandSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type StudentDetail = z.infer<typeof StudentDetailSchema>;
